import React from 'react';
import { Link } from 'react-router-dom';
import {
  Plane,
  Hotel,
  Anchor,
  MapPin,
  CalendarClock,
  Car,
  Compass,
  Wifi,
  Calculator,
  MessageCircle,
  Phone,
  ArrowRight
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { contactInfo } from '../mock/fareData';

/*
 * 2026-06-26 — Dedicated /services landing page.
 * Resolves the broken `/services` internal link in the "Hey Taxi!" blog post.
 * Content is informational only (no fare changes, no booking logic changes).
 * CTAs point to existing routes: /book, /fare-calculator, WhatsApp, tel:.
 */

const services = [
  {
    icon: Plane,
    title: 'Airport Transfers',
    desc: 'Reliable rides to and from Lynden Pindling International Airport (LPIA) at official government rates. Meet-and-greet available for arrivals.'
  },
  {
    icon: Hotel,
    title: 'Hotel Pickups',
    desc: 'Door-to-door service from every major Nassau and Cable Beach hotel. Just share your pickup time and we will be waiting curbside.'
  },
  {
    icon: Anchor,
    title: 'Cruise Port Rides',
    desc: 'Prompt taxi service to and from Prince George Wharf for cruise guests, with help loading luggage and clear pricing before you ride.'
  },
  {
    icon: MapPin,
    title: 'Paradise Island Rides',
    desc: 'Comfortable transfers across the Paradise Island bridge to Atlantis, Ocean Club, and surrounding resorts. Bridge toll quoted upfront.'
  },
  {
    icon: CalendarClock,
    title: 'Scheduled Taxi Service',
    desc: 'Book ahead for a specific date and time — perfect for early morning departures, business meetings, and group transfers.'
  },
  {
    icon: Car,
    title: 'Private Transportation',
    desc: 'Private rides for individuals, couples, families, and small groups. Clean, modern vehicles with professional Bahamian drivers.'
  },
  {
    icon: Compass,
    title: 'Personalized Nassau Tours',
    desc: 'Sightseeing rides on your schedule — Downtown, Fort Charlotte, Cable Beach, Junkanoo Beach, and local hidden gems. Ask about custom routes.'
  },
  {
    icon: Wifi,
    title: 'Free Wi-Fi on Board',
    desc: 'Stay connected on every ride. Complimentary in-vehicle Wi-Fi is available where applicable so you can check in, stream, or work on the go.'
  }
];

const Services = () => {
  return (
    <>
      <SEOHead
        title="Our Taxi Services in Nassau | Taxi-98 Bahamas"
        description="Explore Taxi-98 services in Nassau, The Bahamas — LPIA airport transfers, hotel pickups, cruise port rides, Paradise Island transfers, scheduled rides, private transportation, and personalized Nassau tours. Free Wi-Fi where applicable."
        canonical="/services"
      />

      <main
        data-testid="services-page"
        className="relative min-h-screen bg-gradient-to-br from-cyan-50 via-white to-blue-50 pt-28 pb-20 sm:pt-32 sm:pb-28"
      >
        {/* Subtle accents */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-400/5 rounded-full blur-3xl pointer-events-none"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="max-w-3xl mx-auto text-center mb-14 sm:mb-16">
            <span
              className="inline-block bg-cyan-500/10 border border-cyan-400/30 text-cyan-700 text-xs font-bold tracking-[0.22em] uppercase px-4 py-1.5 rounded-full mb-5"
              data-testid="services-page-eyebrow"
            >
              Our Services
            </span>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-5"
              data-testid="services-page-headline"
            >
              Taxi Services in Nassau
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 leading-relaxed">
              Professional, reliable taxi service across New Providence — from
              LPIA airport transfers to Paradise Island and beyond. Transparent
              pricing, friendly drivers, and clean modern vehicles for every ride.
            </p>
          </div>

          {/* Services grid */}
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 mb-16"
            data-testid="services-page-grid"
          >
            {services.map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                data-testid={`services-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
                className="group bg-white border border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-lg hover:border-cyan-300 transition-all duration-300"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-cyan-500/10 text-cyan-600 mb-4 group-hover:bg-cyan-500/15 transition-colors">
                  <Icon className="w-6 h-6" />
                </div>
                <h2 className="text-lg font-bold text-gray-900 mb-2">{title}</h2>
                <p className="text-sm text-gray-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>

          {/* CTA panel */}
          <div
            className="bg-gradient-to-br from-cyan-600 to-blue-700 rounded-3xl p-8 sm:p-10 lg:p-12 text-white shadow-xl"
            data-testid="services-cta-panel"
          >
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3">
                Ready to ride?
              </h2>
              <p className="text-base sm:text-lg text-cyan-50 mb-8">
                Book your ride online, estimate your fare in seconds, or reach a
                real person on WhatsApp or by phone. We are happy to help with
                anything special you need.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
                <Link
                  to="/book"
                  data-testid="services-cta-book-now"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white text-cyan-700 hover:bg-cyan-50 px-6 py-3 rounded-lg font-semibold text-base transition-all duration-300 shadow-md"
                >
                  <span>Book Now</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>

                <Link
                  to="/fare-calculator"
                  data-testid="services-cta-fare-calculator"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-cyan-700/40 border border-white/30 hover:bg-cyan-700/60 text-white px-6 py-3 rounded-lg font-semibold text-base transition-all duration-300 backdrop-blur-sm"
                >
                  <Calculator className="w-4 h-4" />
                  <span>Fare Calculator</span>
                </Link>

                <a
                  href={`https://wa.me/${contactInfo.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(
                    'Hello Taxi-98, I would like to book a ride.'
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="services-cta-whatsapp"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-semibold text-base transition-all duration-300 shadow-md"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span>WhatsApp Us</span>
                </a>
              </div>

              <div className="mt-7 pt-7 border-t border-white/20 text-sm sm:text-base text-cyan-50">
                <p className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3">
                  <span className="inline-flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    <a
                      href={`tel:${contactInfo.phone.replace(/[^+\d]/g, '')}`}
                      data-testid="services-cta-phone"
                      className="font-semibold hover:underline"
                    >
                      {contactInfo.phone}
                    </a>
                  </span>
                  <span className="hidden sm:inline opacity-50">•</span>
                  <span className="opacity-90">{contactInfo.hours}</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default Services;
